import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DataTable, Modal, Notice, StatusPill } from '../patterns';
import type { StatusVariant } from '../patterns';
import { listOrders, updateOrder, deleteOrder } from '../../services/nutritionService';
import type { FertilizerOrder } from '../../services/nutritionService';
import { formatDate } from '../../dateFormat';
import { formatMeasure } from '../../utils/formatMeasure';
import './nutrition.css';

const orderStatusVariant = (status: FertilizerOrder['status']): StatusVariant => {
  if (status === 'ordered') return 'warn';
  if (status === 'received') return 'ok';
  return 'queued';
};

const formatLines = (lines: FertilizerOrder['lines']): string => {
  if (!lines.length) return '—';
  const first = lines[0].productName ?? lines[0].productId;
  if (lines.length === 1) return first;
  return `${first} +${lines.length - 1}`;
};

/**
 * The full line breakdown behind the "first product +N" summary.
 *
 * An order is a purchasing commitment, so it must be possible to confirm what is
 * on it. Before this the only route to the rest was a `title` tooltip carrying
 * names without quantities — nothing at all on a touch device, and
 * undiscoverable on a desktop.
 *
 * Rendered TWICE, deliberately: once by DataTable's `expandedRowRender` and once
 * inside `mobileCardRow`. The expansion row is a <tr> inside the <table>, and
 * Patterns.css hides that whole table below 768px whenever a mobileCardRow is
 * supplied — so a phone would get a moving chevron and no detail. The two copies
 * carry different test ids because in jsdom (which applies no CSS) both exist at
 * once.
 */
const OrderLineDetail: React.FC<{ order: FertilizerOrder; testId: string }> = ({ order, testId }) => {
  const { t } = useTranslation();
  if (!order.lines.length) {
    return (
      <div className="ntr-order-detail" data-testid={testId}>
        {t('nutrition.inventory.orders.detail.noLines', { defaultValue: 'This order has no lines.' })}
      </div>
    );
  }
  return (
    <ul className="ntr-order-detail" data-testid={testId}>
      {order.lines.map((l) => (
        <li key={l.productId}>
          <span className="ntr-order-detail__name">{l.productName ?? l.productId}</span>
          <span className="ntr-order-detail__qty">{formatMeasure(l.qty, l.unit)}</span>
        </li>
      ))}
    </ul>
  );
};

const NutritionInventoryOrders: React.FC = () => {
  const { t } = useTranslation();
  const [orders, setOrders] = useState<FertilizerOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [actionError, setActionError] = useState('');
  const [confirmOrder, setConfirmOrder] = useState<FertilizerOrder | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<FertilizerOrder | null>(null);
  const [transitioning, setTransitioning] = useState(false);
  // A Set, not a single key: DataTable's expandedRowKey collapses the previous
  // row when handed a plain string, and a manager comparing two orders needs
  // both open. Rebuilt via Array.from so React sees a new reference.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggleExpanded = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(Array.from(prev));
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const load = useCallback(async () => {
    try {
      const data = await listOrders();
      setOrders(data);
      setLoadError('');
    } catch (err: unknown) {
      const e = err as { message?: string };
      setLoadError(e.message ?? 'Failed to load');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleMarkOrdered = async (order: FertilizerOrder) => {
    setActionError('');
    setTransitioning(true);
    try {
      await updateOrder(order._id, 'ordered');
      await load();
    } catch (err: unknown) {
      const e = err as { message?: string };
      setActionError(e.message ?? t('nutrition.inventory.orders.actionError'));
    } finally {
      setTransitioning(false);
    }
  };

  const handleConfirmReceive = async () => {
    if (!confirmOrder) return;
    setActionError('');
    setTransitioning(true);
    try {
      await updateOrder(confirmOrder._id, 'received');
      setConfirmOrder(null);
      await load();
    } catch (err: unknown) {
      const e = err as { message?: string };
      setActionError(e.message ?? t('nutrition.inventory.orders.actionError'));
    } finally {
      setTransitioning(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setActionError('');
    setTransitioning(true);
    try {
      await deleteOrder(deleteTarget._id);
      setDeleteTarget(null);
      await load();
    } catch (err: unknown) {
      const e = err as { message?: string };
      setActionError(e.message ?? t('nutrition.inventory.orders.actionError'));
    } finally {
      setTransitioning(false);
    }
  };

  if (loading) return <div>{t('common.loading')}</div>;
  if (loadError) return <Notice tone="danger" role="alert">{loadError}</Notice>;

  return (
    <div data-testid="nutrition-inventory-orders">
      {actionError && (
        <Notice tone="danger" role="alert">{actionError}</Notice>
      )}

      <DataTable<FertilizerOrder>
        rows={orders}
        rowKey={(o) => o._id}
        columns={[
          {
            key: 'created',
            label: t('nutrition.inventory.orders.col.created', { defaultValue: 'Created' }),
            render: (o) => formatDate(o.createdAt),
          },
          {
            key: 'orderBy',
            label: t('nutrition.inventory.orders.col.orderBy', { defaultValue: 'Order-by' }),
            render: (o) => o.orderBy ? formatDate(o.orderBy, { timeZone: 'UTC' }) : '—',
          },
          {
            key: 'lines',
            label: t('nutrition.inventory.orders.col.lines', { defaultValue: 'Lines' }),
            render: (o) => (
              <button
                type="button"
                className="ntr-order-lines-toggle"
                data-testid={`order-lines-toggle-${o._id}`}
                aria-expanded={expanded.has(o._id)}
                aria-label={t('nutrition.inventory.orders.detail.toggle', { defaultValue: 'Show order lines' })}
                onClick={() => toggleExpanded(o._id)}
              >
                <span aria-hidden="true">{expanded.has(o._id) ? '▾' : '▸'}</span>{' '}
                {o.lines.length} · {formatLines(o.lines)}
              </button>
            ),
          },
          {
            key: 'status',
            label: t('nutrition.inventory.orders.col.status', { defaultValue: 'Status' }),
            render: (o) => (
              <StatusPill variant={orderStatusVariant(o.status)}>
                {t(`nutrition.inventory.orders.status.${o.status}`, { defaultValue: o.status })}
              </StatusPill>
            ),
          },
          {
            key: 'actions',
            label: t('common.actions', { defaultValue: 'Actions' }),
            render: (o) => {
              if (o.status === 'draft') {
                return (
                  <span style={{ display: 'inline-flex', gap: 8 }}>
                    <button
                      type="button"
                      className="pat-btn pat-btn--primary"
                      disabled={transitioning}
                      onClick={() => handleMarkOrdered(o)}
                    >
                      {t('nutrition.inventory.orders.action.markOrdered', { defaultValue: 'Mark ordered' })}
                    </button>
                    <button
                      type="button"
                      className="pat-btn pat-btn--subtle"
                      disabled={transitioning}
                      onClick={() => setDeleteTarget(o)}
                    >
                      {t('nutrition.inventory.orders.action.delete', { defaultValue: 'Delete' })}
                    </button>
                  </span>
                );
              }
              if (o.status === 'ordered') {
                return (
                  <span style={{ display: 'inline-flex', gap: 8 }}>
                    <button
                      type="button"
                      className="pat-btn pat-btn--primary"
                      disabled={transitioning}
                      onClick={() => setConfirmOrder(o)}
                    >
                      {t('nutrition.inventory.orders.action.markReceived', { defaultValue: 'Mark received' })}
                    </button>
                    <button
                      type="button"
                      className="pat-btn pat-btn--subtle"
                      disabled={transitioning}
                      onClick={() => setDeleteTarget(o)}
                    >
                      {t('nutrition.inventory.orders.action.delete', { defaultValue: 'Delete' })}
                    </button>
                  </span>
                );
              }
              return null;
            },
          },
        ]}
        emptyState={t('nutrition.inventory.orders.empty', { defaultValue: 'No orders yet.' })}
        expandedRowRender={(o) => <OrderLineDetail order={o} testId={`order-detail-${o._id}`} />}
        expandedRowKey={expanded}
        mobileCardRow={(o) => (
          <div>
            <strong>
              {formatDate(o.createdAt)} ·{' '}
              {t(`nutrition.inventory.orders.status.${o.status}`, { defaultValue: o.status })}
            </strong>
            <button
              type="button"
              className="ntr-order-lines-toggle"
              data-testid={`order-lines-card-toggle-${o._id}`}
              aria-expanded={expanded.has(o._id)}
              aria-label={t('nutrition.inventory.orders.detail.toggle', { defaultValue: 'Show order lines' })}
              onClick={() => toggleExpanded(o._id)}
            >
              <span aria-hidden="true">{expanded.has(o._id) ? '▾' : '▸'}</span>{' '}
              {o.lines.length} {t('nutrition.inventory.orders.col.lines', { defaultValue: 'Lines' })} · {formatLines(o.lines)}
            </button>
            {expanded.has(o._id) && (
              <OrderLineDetail order={o} testId={`order-detail-card-${o._id}`} />
            )}

            {o.orderBy && (
              <div style={{ fontSize: 11, color: 'var(--shell-text-muted)' }}>
                {t('nutrition.inventory.orders.col.orderBy', { defaultValue: 'Order-by' })}: {formatDate(o.orderBy, { timeZone: 'UTC' })}
              </div>
            )}
          </div>
        )}
      />

      <Modal
        show={!!confirmOrder}
        onHide={() => setConfirmOrder(null)}
        title={t('nutrition.inventory.orders.receiveConfirm.title', { defaultValue: 'Confirm receipt' })}
      >
        <Modal.Body>
          <p>
            {t('nutrition.inventory.orders.receiveConfirm.body', {
              defaultValue: 'Receiving this order will write stock into inventory. This action cannot be undone.',
            })}
          </p>
          {confirmOrder && (
            <p style={{ fontSize: 13, color: 'var(--shell-text-muted)' }}>
              {confirmOrder.lines.length}{' '}
              {t('nutrition.inventory.orders.col.lines', { defaultValue: 'Lines' })}:{' '}
              {confirmOrder.lines.map((l) => l.productName ?? l.productId).join(', ')}
            </p>
          )}
          {actionError && (
            <Notice tone="danger" role="alert">{actionError}</Notice>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="pat-btn pat-btn--subtle"
            onClick={() => setConfirmOrder(null)}
            disabled={transitioning}
          >
            {t('common.cancel', { defaultValue: 'Cancel' })}
          </button>
          <button
            type="button"
            className="pat-btn pat-btn--primary"
            onClick={handleConfirmReceive}
            disabled={transitioning}
          >
            {t('common.confirm', { defaultValue: 'Confirm' })}
          </button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={!!deleteTarget}
        onHide={() => setDeleteTarget(null)}
        title={t('nutrition.inventory.orders.deleteConfirm.title', { defaultValue: 'Delete order?' })}
      >
        <Modal.Body>
          <p>
            {t('nutrition.inventory.orders.deleteConfirm.body', {
              defaultValue: 'Deleting a draft order frees its quantity back into order-to-need and cannot be undone.',
            })}
          </p>
          {deleteTarget && (
            <p style={{ fontSize: 13, color: 'var(--shell-text-muted)' }}>
              {deleteTarget.lines.length}{' '}
              {t('nutrition.inventory.orders.col.lines', { defaultValue: 'Lines' })}:{' '}
              {deleteTarget.lines.map((l) => l.productName ?? l.productId).join(', ')}
            </p>
          )}
          {actionError && (
            <Notice tone="danger" role="alert">{actionError}</Notice>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="pat-btn pat-btn--subtle"
            onClick={() => setDeleteTarget(null)}
            disabled={transitioning}
          >
            {t('common.cancel', { defaultValue: 'Cancel' })}
          </button>
          <button
            type="button"
            className="pat-btn pat-btn--subtle"
            onClick={handleConfirmDelete}
            disabled={transitioning}
          >
            {t('nutrition.inventory.orders.action.delete', { defaultValue: 'Delete' })}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default NutritionInventoryOrders;
